import React, { useState, useEffect } from 'react';
import { PageLayout } from '../../components/organisms/PageLayout';
import ContactCard from '../../components/molecules/contacts/ContactCard';
import ConfirmDeleteModal from '../../components/organisms/ConfirmDeleteModal';
import ContactsModal from '../../components/molecules/contacts/ContactsModal';
import api from '../../utils/api';
import { API_ENDPOINTS } from '../../config';
import { showToast } from '../../utils/toastManager';
import '../../styles/contacts.css';
import { emitSubscriptionRefresh } from '../../utils/subscriptionEvents';

interface ContactFromAPI {
  uid: string;
  first_name: string;
  last_name: string;
  job_title?: string;
  companies_name?: string[];
  email?: string[];
  phone?: string[];
  linkedin_url?: string;
  twitter_url?: string;
  meta_url?: string;
  github_url?: string;
  board_linked?: string;
  job_linked?: string;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  title: string;
  company: string;
  email: string;
  phone: string;
  location?: string;
  twitter?: string;
  linkedin?: string;
  facebook?: string;
  github?: string;
  boardLinked?: string;
  jobLinked?: string;
}

interface ContactModalData {
  firstName?: string;
  lastName?: string;
  title?: string;
  company?: string;
  location?: string;
  email?: string;
  phone?: string;
  twitter?: string;
  facebook?: string;
  linkedin?: string;
  github?: string;
  boardLinked?: string;
  jobLinked?: string;
}

const Contacts: React.FC = () => {
  const breadcrumbItems = [
    { label: "Home", href: "/" },
    { label: "Contacts" }
  ];

  useEffect(() => {
    document.title = 'OfferFlow | Contacts';
  }, []);

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    contactId: string | null;
    contactName: string;
  }>({
    isOpen: false,
    contactId: null,
    contactName: '',
  });
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    contactId: string | null;
    contactData: ContactModalData | null;
  }>({
    isOpen: false,
    contactId: null,
    contactData: null,
  });
  const [createModal, setCreateModal] = useState<boolean>(false);

  const transformContact = (apiContact: ContactFromAPI): Contact => {
    const transformed = {
      id: apiContact.uid,
      firstName: apiContact.first_name,
      lastName: apiContact.last_name,
      name: `${apiContact.first_name} ${apiContact.last_name}`.trim(),
      title: apiContact.job_title || '',
      company: apiContact.companies_name?.[0] || '',
      email: apiContact.email?.[0] || '',
      phone: apiContact.phone?.[0] || '',
      twitter: apiContact.twitter_url || undefined,
      linkedin: apiContact.linkedin_url || undefined,
      facebook: apiContact.meta_url || undefined,
      github: apiContact.github_url || undefined,
      boardLinked: apiContact.board_linked,
      jobLinked: apiContact.job_linked,
    };
    
    return transformed;
  };

  useEffect(() => {
    let cancelled = false;
    const fetchContacts = async () => {
      try {
        setLoadingContacts(true);
        const data = await api.get<ContactFromAPI[]>(API_ENDPOINTS.contacts.list);
        if (cancelled) return;

        if (Array.isArray(data)) {
          setContacts(data.map(transformContact));
        } else {
          setContacts([]);
        }
      } catch (error) {
        if (cancelled) return;
        setContacts([]);
      } finally {
        if (!cancelled) setLoadingContacts(false);
      }
    };

    fetchContacts();
    return () => { cancelled = true; };
  }, []);

  const handleDeleteClick = (id: string, name: string) => {
    setDeleteModal({
      isOpen: true,
      contactId: id,
      contactName: name,
    });
  };

  const handleConfirmDelete = async () => {
    if (deleteModal.contactId === null) return;

    try {
      await api.delete(API_ENDPOINTS.contacts.delete(deleteModal.contactId));

      setContacts(contacts.filter((contact) => contact.id !== deleteModal.contactId));

      emitSubscriptionRefresh();

      showToast('Contact deleted successfully', 'success');

      setDeleteModal({
        isOpen: false,
        contactId: null,
        contactName: '',
      });
    } catch (error: any) {
      if (error?.status === 404) {
        setContacts(contacts.filter((contact) => contact.id !== deleteModal.contactId));
        setDeleteModal({
          isOpen: false,
          contactId: null,
          contactName: '',
        });
        showToast('Contact was already deleted', 'info');
      } else {
        showToast('Failed to delete contact. Please try again.', 'error');
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.error('Delete contact error:', error);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteModal({
      isOpen: false,
      contactId: null,
      contactName: '',
    });
  };

  const handleEditClick = (id: string) => {
    const contact = contacts.find((c) => c.id === id);
    if (!contact) return;

    const modalData: ContactModalData = {
      firstName: contact.firstName,
      lastName: contact.lastName,
      title: contact.title,
      company: contact.company,
      email: contact.email || undefined,
      phone: contact.phone || undefined,
      twitter: contact.twitter || undefined,
      linkedin: contact.linkedin || undefined,
      facebook: contact.facebook || undefined,
      github: contact.github || undefined,
      boardLinked: contact.boardLinked,
      jobLinked: contact.jobLinked,
    };

    setEditModal({
      isOpen: true,
      contactId: id,
      contactData: modalData,
    });
  };

  const handleEditSubmit = async (data: ContactModalData) => {
    if (editModal.contactId === null) return;
    
    try {
      if (!data.firstName?.trim()) {
        showToast('First name is required', 'error');
        return;
      }
      
      if (!data.lastName?.trim()) {
        showToast('Last name is required', 'error');
        return;
      }
      
      if (data.email && !isValidEmail(data.email)) {
        showToast('Please enter a valid email address', 'error');
        return;
      }
      
      const contactPayload = {
        first_name: data.firstName.trim(),
        last_name: data.lastName.trim(),
        job_title: data.title?.trim() || null,
        companies_name: data.company?.trim() ? [data.company.trim()] : null,
        email: data.email?.trim() ? [data.email.trim()] : null,
        phone: data.phone?.trim() ? [data.phone.trim()] : null,
        linkedin_url: data.linkedin?.trim() || null,
        twitter_url: data.twitter?.trim() || null,
        meta_url: data.facebook?.trim() || null,
        github_url: data.github?.trim() || null,
        board_linked: data.boardLinked || null,
        job_linked: data.jobLinked || null,
      };

      await api.put(API_ENDPOINTS.contacts.update(editModal.contactId), contactPayload);
      
      const firstName = data.firstName?.trim() || '';
      const lastName = data.lastName?.trim() || '';
      
      setContacts(
        contacts.map((contact) => {
          if (contact.id === editModal.contactId) {
            return {
              ...contact,
              firstName,
              lastName,
              name: `${firstName} ${lastName}`.trim(),
              title: data.title || '',
              company: data.company || '',
              email: data.email || '',
              phone: data.phone || '',
              twitter: data.twitter || undefined,
              linkedin: data.linkedin || undefined,
              facebook: data.facebook || undefined,
              github: data.github || undefined,
              boardLinked: data.boardLinked,
              jobLinked: data.jobLinked,
            };
          }
          return contact;
        })
      );
      
      showToast('Contact updated successfully', 'success');
      
      setEditModal({
        isOpen: false,
        contactId: null,
        contactData: null,
      });
    } catch (error: any) {
      showToast('Failed to update contact. Please try again.', 'error');
      
      if (process.env.NODE_ENV === 'development') {
        console.error('Edit contact error:', error);
      }
    }
  };

  const handleEditClose = () => {
    setEditModal({
      isOpen: false,
      contactId: null,
      contactData: null,
    });
  };

  const handleCreateClick = () => {
    setCreateModal(true);
  };

  const handleCreateSubmit = async (data: ContactModalData) => {
    try {
      if (!data.firstName?.trim()) {
        showToast('First name is required', 'error');
        return;
      }
      
      if (!data.lastName?.trim()) {
        showToast('Last name is required', 'error');
        return;
      }
      
      if (data.email && !isValidEmail(data.email)) {
        showToast('Please enter a valid email address', 'error');
        return;
      }
      
      const contactPayload = {
        first_name: data.firstName.trim(),
        last_name: data.lastName.trim(),
        job_title: data.title?.trim() || null,
        companies_name: data.company?.trim() ? [data.company.trim()] : null,
        email: data.email?.trim() ? [data.email.trim()] : null,
        phone: data.phone?.trim() ? [data.phone.trim()] : null,
        linkedin_url: data.linkedin?.trim() || null,
        twitter_url: data.twitter?.trim() || null,
        meta_url: data.facebook?.trim() || null,
        github_url: data.github?.trim() || null,
        board_linked: data.boardLinked || null,
        job_linked: data.jobLinked || null,
      };

      await api.post(API_ENDPOINTS.contacts.create, contactPayload);
      
      const updatedContacts = await api.get<ContactFromAPI[]>(API_ENDPOINTS.contacts.list);
      if (Array.isArray(updatedContacts)) {
        const transformedContacts = updatedContacts.map(transformContact);
        setContacts(transformedContacts);
      }
      
      emitSubscriptionRefresh();
      
      showToast('Contact created successfully', 'success');
      
      setCreateModal(false);
    } catch (error: any) {
      showToast('Failed to create contact. Please try again.', 'error');
      
      if (process.env.NODE_ENV === 'development') {
        console.error('Create contact error:', error);
      }
    }
  };
  
  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleCreateClose = () => {
    setCreateModal(false);
  };

  return (
    <PageLayout 
      breadcrumbItems={breadcrumbItems}
      actions={
        <button className="breadcrumbs__action-btn" onClick={handleCreateClick}>
          + Contact
        </button>
      }
    >
      <div className="contacts-container">
        {loadingContacts ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            Loading contacts...
          </div>
        ) : contacts.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            No contacts yet. Click "+ Contact" to create one!
          </div>
        ) : (
          <div className="contacts-grid">
            {contacts.map((contact) => (
              <ContactCard
                key={contact.id}
                id={contact.id}
                firstName={contact.firstName}
                lastName={contact.lastName}
                name={contact.name}
                title={contact.title}
                company={contact.company}
                email={contact.email}
                phone={contact.phone}
                location={contact.location}
                twitter={contact.twitter}
                linkedin={contact.linkedin}
                facebook={contact.facebook}
                github={contact.github}
                onEdit={() => handleEditClick(contact.id)}
                onDelete={() => handleDeleteClick(contact.id, contact.name)}
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmDeleteModal
        isOpen={deleteModal.isOpen}
        itemName={deleteModal.contactName}
        itemType="Contact"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <ContactsModal
        open={editModal.isOpen}
        initial={editModal.contactData}
        onClose={handleEditClose}
        onSubmit={handleEditSubmit}
      />

      <ContactsModal
        open={createModal}
        initial={null}
        onClose={handleCreateClose}
        onSubmit={handleCreateSubmit}
      />
    </PageLayout>
  );
};

export default Contacts;
